import type {CreateExtArgs, CreateExtensionArgument, DmApis, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {Pointer, CompRef} from '@wix/document-services-types'
import {getPageOutlineAndTargetsForBuilder, type BuilderOutlineWithIdMapAndTargets} from '../aiBuilderGeneration'
import type {DataExtensionAPI} from '../../data'
import type {UndoRedoExtApi} from '../../undoRedo'
import type {BuilderStyleExtensionAPI} from '../../builderComponents/builderStyle'
import type {InnerElementsExtensionAPI} from '../../builderComponents/innerElements'
import {
    analyze,
    recommend,
    buildSiteContext,
    type SiteContext,
    type AnalyzeParams,
    type AnalysisResult,
    type RecommendParams,
    type RecommendationResult,
    type Recommendation,
    type ApplyRecommendationsResult,
    type SuggestParams,
    RecommendationActionType
} from './index'
import type {FragmentsExtensionAPI} from '../../fragments/fragments'
import {createSiteContextManager, type GetSiteContextOptions} from './siteContextManager'

const DEFAULT_MAX_RECOMMENDATIONS = 20
const DEFAULT_SCORE_THRESHOLD = 0.3

export interface ProactiveAssistanceApi {
    getSiteContext(options?: GetSiteContextOptions): Promise<SiteContext>
    buildSiteContext(): Promise<SiteContext>
    extractContent(containerPointer: Pointer): BuilderOutlineWithIdMapAndTargets
    analyze(params: AnalyzeParams): Promise<AnalysisResult>
    recommend(params: RecommendParams): Promise<RecommendationResult>
    suggest(params: SuggestParams): Promise<RecommendationResult>
    applyRecommendations(recommendations: Recommendation[]): ApplyRecommendationsResult
}

export type ProactiveAssistanceExtApi = ExtensionAPI & {proactiveAssistance: ProactiveAssistanceApi}

const createExtension = ({environmentContext}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = (createExtArgs: CreateExtArgs): ProactiveAssistanceExtApi => {
        const {dal, extensionAPI} = createExtArgs
        const {fragments} = extensionAPI as FragmentsExtensionAPI

        const siteContextManager = createSiteContextManager({
            dal,
            fragments,
            environmentContext,
            createExtArgs
        })

        const suggest = async (params: SuggestParams): Promise<RecommendationResult> => {
            const {
                targetPointer,
                maxRecommendations = DEFAULT_MAX_RECOMMENDATIONS,
                scoreThreshold = DEFAULT_SCORE_THRESHOLD
            } = params

            const siteContext = await siteContextManager.getSiteContext()
            const analysisResult = await analyze(createExtArgs, environmentContext, {targetPointer, siteContext})

            return recommend(environmentContext, {analysisResult, maxRecommendations, scoreThreshold})
        }

        const handleAction = (recommendation: Recommendation): void => {
            const {componentPointer, action} = recommendation
            const {data: actionData, type, innerElementPath} = action
            switch (type) {
                case RecommendationActionType.TEXT: {
                    const {data} = createExtArgs.extensionAPI as DataExtensionAPI
                    data.update(componentPointer as CompRef, actionData)
                    break
                }
                case RecommendationActionType.STYLE: {
                    const {builderStyle} = createExtArgs.extensionAPI as BuilderStyleExtensionAPI
                    builderStyle.component.updateCSSStyle(componentPointer as CompRef, actionData)
                    break
                }
                case RecommendationActionType.INNER_ELEMENT_STYLE: {
                    const {innerElements} = createExtArgs.extensionAPI as InnerElementsExtensionAPI
                    innerElements.setInnerElementStyle(componentPointer as CompRef, innerElementPath ?? [], actionData)
                    break
                }
                default:
                    throw Error('Unsupported action type')
            }
        }

        const applyRecommendations = (recommendations: Recommendation[]): ApplyRecommendationsResult => {
            const {undoRedo} = createExtArgs.extensionAPI as UndoRedoExtApi

            return recommendations.map(recommendation => {
                const {componentPointer, action} = recommendation

                try {
                    handleAction(recommendation)
                    const snapshotRef = undoRedo.preAdd(`AI Recommendation: ${recommendation.id}`)
                    undoRedo.executeAdd(snapshotRef)

                    return {success: true, componentPointer, appliedValue: action.data}
                } catch (e: any) {
                    return {
                        success: false,
                        componentPointer,
                        appliedValue: null,
                        error: e?.message ?? 'Failed to apply recommendation'
                    }
                }
            })
        }

        const buildSiteContextWithLog = async () => {
            return buildSiteContext(environmentContext, createExtArgs)
        }

        return {
            proactiveAssistance: {
                getSiteContext: siteContextManager.getSiteContext,
                buildSiteContext: buildSiteContextWithLog,
                extractContent: (containerPointer: Pointer) =>
                    getPageOutlineAndTargetsForBuilder(createExtArgs, containerPointer),
                analyze: (params: AnalyzeParams) => analyze(createExtArgs, environmentContext, params),
                recommend: (params: RecommendParams) => recommend(environmentContext, params),
                suggest,
                applyRecommendations
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {proactiveAssistance} = extensionAPI as ProactiveAssistanceExtApi
        return {
            ai: {
                proactiveAssistance: {
                    getSiteContext: proactiveAssistance.getSiteContext,
                    buildSiteContext: proactiveAssistance.buildSiteContext,
                    extractContent: proactiveAssistance.extractContent,
                    analyze: proactiveAssistance.analyze,
                    recommend: proactiveAssistance.recommend,
                    suggest: proactiveAssistance.suggest,
                    applyRecommendations: proactiveAssistance.applyRecommendations
                }
            }
        }
    }

    return {
        name: 'proactiveAssistance',
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
